import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Collapse,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  BugReport as DebugIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DirectionsCar as CarIcon,
  CalendarMonth as YearIcon,
  Category as SeriesIcon,
  Place as RegionIcon,
  Star as FeatureIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiService } from '../../services/api';
import { CVIMessage, CVISelectionState, CVIToolTrace } from '../../types';

const INITIAL_SELECTION: CVISelectionState = {
  brand_division: null,
  year: null,
  series: null,
  region: null,
  feature: null,
};

const SELECTION_ICONS: Record<string, React.ReactNode> = {
  brand_division: <CarIcon fontSize="small" />,
  year: <YearIcon fontSize="small" />,
  series: <SeriesIcon fontSize="small" />,
  region: <RegionIcon fontSize="small" />,
  feature: <FeatureIcon fontSize="small" />,
};

const SELECTION_LABELS: Record<string, string> = {
  brand_division: 'Brand',
  year: 'Year',
  series: 'Series',
  region: 'Region',
  feature: 'Feature',
};

export const CVIAgentView: React.FC = () => {
  const [messages, setMessages] = useState<CVIMessage[]>([
    { role: 'assistant', content: "Hi! I can help you explore Toyota and Lexus vehicle availability and customer preferences. Ask me about specific models, years, regions, or features!" }
  ]);
  const [selection, setSelection] = useState<CVISelectionState>(INITIAL_SELECTION);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [toolTrace, setToolTrace] = useState<CVIToolTrace[]>([]);
  const [showTrace, setShowTrace] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim() || loading) return;

    const userMsg: CVIMessage = { role: 'user', content: inputValue };
    const newHistory = [...messages, userMsg];

    setMessages(newHistory);
    setInputValue('');
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.sendCVIMessage(newHistory, selection, debugMode);

      const assistantMsgs = response.messages.filter((m: CVIMessage) => m.role === 'assistant');
      setMessages([...newHistory, ...assistantMsgs]);
      setSelection(response.selection || selection);

      if (response.tool_trace) {
        setToolTrace(prev => [...prev, ...response.tool_trace]);
      }
    } catch (err: any) {
      console.error(err);
      setError('Failed to get response. Please try again.');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I encountered an error processing your request. Please try again." 
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleReset = () => {
    setMessages([{ 
      role: 'assistant', 
      content: "Conversation reset! How can I help you explore vehicle availability?" 
    }]);
    setSelection(INITIAL_SELECTION);
    setToolTrace([]);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const activeSelections = Object.entries(selection).filter(([_, value]) => value !== null);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 180px)', gap: 2 }}>
      {/* Sidebar */}
      <Paper
        elevation={0}
        sx={{
          width: 300,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Sidebar Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8fafc' }}>
          <Typography variant="subtitle1" fontWeight={600} color="primary">
            Query Context
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current selection filters
          </Typography>
        </Box>

        {/* Selection Cards */}
        <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
          {activeSelections.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {activeSelections.map(([key, value]) => (
                <Card
                  key={key}
                  variant="outlined"
                  sx={{
                    borderColor: 'primary.light',
                    bgcolor: 'primary.50',
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: 'primary.main' }}>
                        {SELECTION_ICONS[key]}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.5px' }}>
                          {SELECTION_LABELS[key]}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="primary.dark">
                          {value}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <CarIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
              <Typography variant="body2">
                No filters active
              </Typography>
              <Typography variant="caption">
                Ask about a vehicle to start filtering
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Debug Toggle */}
        <Box sx={{ p: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={debugMode}
                onChange={(e) => setDebugMode(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DebugIcon fontSize="small" sx={{ opacity: 0.7 }} />
                <Typography variant="body2">Debug Mode</Typography>
              </Box>
            }
          />

          {debugMode && toolTrace.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                startIcon={showTrace ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowTrace(!showTrace)}
                sx={{ textTransform: 'none' }}
              >
                Tool Trace ({toolTrace.length})
              </Button>
              <Collapse in={showTrace}>
                <Box
                  sx={{
                    mt: 1,
                    maxHeight: 200,
                    overflowY: 'auto',
                    bgcolor: '#1e1e1e',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {toolTrace.slice(-5).map((t, i) => (
                    <Box key={i} sx={{ mb: 1, fontSize: '11px', fontFamily: 'monospace', color: '#d4d4d4' }}>
                      <Typography component="span" sx={{ color: '#569cd6', fontFamily: 'inherit', fontSize: 'inherit' }}>
                        {t.tool}
                      </Typography>
                      <Typography component="pre" sx={{ 
                        color: '#9cdcfe', 
                        fontFamily: 'inherit', 
                        fontSize: '10px',
                        m: 0,
                        mt: 0.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all'
                      }}>
                        {JSON.stringify(t.args, null, 2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Reset Button */}
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            sx={{ textTransform: 'none' }}
          >
            Reset Conversation
          </Button>
        </Box>
      </Paper>

      {/* Main Chat Area */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Chat Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          bgcolor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <BotIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Customer Demand Preferences Agent
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Explore Toyota & Lexus availability data
            </Typography>
          </Box>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            bgcolor: '#fafafa',
          }}
        >
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}

          {messages.map((msg, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                gap: 1,
              }}
            >
              {msg.role !== 'user' && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BotIcon sx={{ fontSize: 18, color: 'white' }} />
                </Box>
              )}

              <Paper
                elevation={0}
                sx={{
                  maxWidth: '75%',
                  p: 2,
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'white',
                  color: msg.role === 'user' ? 'white' : 'text.primary',
                  borderRadius: 2,
                  border: msg.role === 'user' ? 'none' : '1px solid',
                  borderColor: 'divider',
                  '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    my: 1,
                    fontSize: '13px',
                    '& th, & td': {
                      border: '1px solid',
                      borderColor: msg.role === 'user' ? 'rgba(255,255,255,0.3)' : 'divider',
                      p: 1,
                      textAlign: 'left',
                    },
                    '& th': {
                      bgcolor: msg.role === 'user' ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
                      fontWeight: 600,
                    },
                  },
                  '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                  '& ul, & ol': { m: 0, pl: 2.5 },
                  '& code': {
                    bgcolor: msg.role === 'user' ? 'rgba(255,255,255,0.2)' : '#f5f5f5',
                    px: 0.5,
                    borderRadius: 0.5,
                    fontSize: '0.875em',
                  },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {msg.content}
                </ReactMarkdown>
              </Paper>

              {msg.role === 'user' && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 18, color: '#666' }} />
                </Box>
              )}
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BotIcon sx={{ fontSize: 18, color: 'white' }} />
              </Box>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Analyzing your request...
                </Typography>
              </Paper>
            </Box>
          )}

          <div ref={bottomRef} />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white',
            display: 'flex',
            gap: 1.5,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Ask about vehicle availability, features, regions..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            inputRef={inputRef}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={loading || !inputValue.trim()}
            sx={{
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: '#ffff !important',
            }}
            endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          >
            Send
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CVIAgentView;